import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import useAos from '../hooks/useAos';
import { PageMeta } from '../seo/PageMeta';
import { useJobs } from '../hooks/useJobs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faChartLine, faShieldHalved, faClock, faScaleBalanced, faGraduationCap, faUsers } from '@fortawesome/free-solid-svg-icons';

const CONDITION_ICONS = [
    [faFileInvoiceDollar, faChartLine, faShieldHalved, faClock],
    [faScaleBalanced, faGraduationCap, faUsers],
];

function Carriere() {
    const { t } = useTranslation(['pages', 'common']);
    const jobsData = useJobs();
    const [openJobs, setOpenJobs] = useState({});

    const conditions = t('pages:careers.conditions', { returnObjects: true });
    const offeredConditions = [
        conditions.slice(0, 4).map((label, index) => ({ icon: CONDITION_ICONS[0][index], label })),
        conditions.slice(4).map((label, index) => ({ icon: CONDITION_ICONS[1][index], label })),
    ];

    const toggleJob = (index) => {
        setOpenJobs((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    return (
        <div className="bg-gray w-full px-64 pt-96 pb-64 overflow-hidden" data-navbar-theme="dark">
            <div className="flex justify-between items-center gap-32 py-64 tablet-flex-column tablet-items-start">
                <div className="w-60 overflow-hidden">
                    <p className="text-title1 t-white fustat text-semibold m-0" data-aos="fade-up">{t('pages:careers.title')}</p>
                </div>
                <p className="text-subtitle2 outfit text-light w-40 t-graylight" data-aos="fade-up" data-aos-delay="150">{t('pages:careers.intro')}</p>
            </div>
            <div data-aos="fade" data-aos-delay="300">
                <hr/>
            </div>

            <div className="flex flex-column justify-center items-center">
                <p className="text-title3 mt-64 mb-16 t-white fustat text-semibold" data-aos="fade-up">{t('pages:careers.offersTitle')}</p>
                <p className="text-subtitle outfit text-regular t-graylight w-70 text-center mb-64" data-aos="fade-up" data-aos-delay="150">{t('pages:careers.offersIntro')}</p>

                <div className="flex flex-column gap-32 w-full self-stretch">
                    {jobsData.map((job, index) => {
                        const isOpen = !!openJobs[index];

                        return (
                        <div key={index} className="bg-white w-full px-32 py-32 border-graylight carriere-job-card" data-aos="fade-up" data-aos-delay={index * 100}>
                            <p className={`text-title3 t-graymain fustat text-semibold m-0 ${isOpen ? 'mb-16' : ''}`}>{job.title}</p>
                            <div className={`flex flex-wrap gap-16 ${isOpen ? 'mb-32' : ''}`}>
                                <p className="text-content px-16 py-8 bg-blue t-white outfit text-light border-radius-10 mt-0">{job.place}</p>
                                <p className="text-content px-16 py-8 bg-blue t-white outfit text-light border-radius-10 mt-0">{job.time}</p>
                            </div>

                            <div className={`service-section-collapse w-full ${isOpen ? 'is-open' : ''}`}>
                                <div className="service-section-collapse__inner">
                                    <div data-aos="fade">
                                        <hr/>
                                    </div>

                                    <div className="job-details-grid mb-64">
                                        <div className="job-section">
                                            <p className="job-section__title text-subtitle2 fustat text-semibold">{t('pages:careers.jobDescription')}</p>
                                            <p className="text-content outfit text-regular t-gray m-0 mb-16">{job.description}</p>
                                        </div>

                                        <div className="job-section">
                                            <p className="job-section__title text-subtitle2 fustat text-semibold">{t('pages:careers.requirements')}</p>
                                            <ul className="job-section__list text-content outfit text-regular t-gray">
                                                {job.requirements.map((requirement, requirementIndex) => (
                                                    <li key={requirementIndex}>{requirement}</li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="job-section">
                                            <p className="job-section__title text-subtitle2 fustat text-semibold">{t('pages:careers.responsibilities')}</p>
                                            <ul className="job-section__list text-content outfit text-regular t-gray">
                                                {job.responsibilities.map((responsibility, responsibilityIndex) => (
                                                    <li key={responsibilityIndex}>{responsibility}</li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="job-conditions">
                                            <p className="job-section__title job-section__title--white text-subtitle2 fustat text-semibold">{t('pages:careers.conditionsOffered')}</p>
                                            <div className="job-conditions__grid">
                                                {offeredConditions.map((column, columnIndex) => (
                                                    <div key={columnIndex} className="flex flex-column gap-24">
                                                        {column.map((condition, conditionIndex) => (
                                                            <div key={conditionIndex} className="job-conditions__item">
                                                                <span className="job-conditions__icon">
                                                                    <FontAwesomeIcon icon={condition.icon} />
                                                                </span>
                                                                <p className="text-content outfit text-light t-white m-0">{condition.label}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div data-aos="fade">
                                        <hr/>
                                    </div>

                                    <div className="mt-32">
                                        <p className="text-subtitle t-graymain fustat text-semibold mb-16 mt-16 text-center m-0">{t('pages:careers.assets')}</p>
                                        <div className="flex flex-wrap justify-center items-center gap-16 mb-32">
                                            {job.atouts.map((atout, atoutIndex) => (
                                                <p className="text-subtitle2 outfit text-regular t-bluelink text-center m-0" key={atoutIndex}>+ {atout}</p>
                                            ))}
                                        </div>
                                    </div>

                                    <div data-aos="fade">
                                        <hr/>
                                    </div>

                                    <div className="flex justify-center items-center">
                                        <p className="text-subtitle text-center w-70 outfit text-regular t-gray m-0 mb-16 mt-32 self-stretch">
                                            {t('pages:careers.contactPromptBefore')}{' '}
                                            <a href="/nousjoindre" className="text-underline">{t('pages:careers.contactPromptLink')}</a>{' '}
                                            {t('pages:careers.contactPromptAfter')}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-center items-center">
                                <button
                                    type="button"
                                    className="btn-light-black"
                                    onClick={() => toggleJob(index)}
                                    >
                                        {isOpen ? t('common:buttons.showLess') : t('common:buttons.learnMore')}
                                </button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default function CarrierePage() {
    useAos();
    const { t } = useTranslation('pages');

    return (
        <>
            <PageMeta
                title={t('meta.careers.title')}
                description={t('meta.careers.description')}
                path="/carrieres"
            />
            <Navbar />
            <Carriere />
            <Footer />
        </>
    );
}
